/* screens/sales/PublicPlanDrawer.jsx — редактор публичного тарифа и три способа создать новый.

   Режимы: 'edit' — правка (PATCH только изменённых полей); 'term' — новый срок
   существующего тарифа; 'new' — новый тариф или акция; 'copy' — копия с новым слагом.
   Правка и новый тариф — по вкладкам (владелец 18.10): «Основное», «Цена и срок»,
   «Лимиты и возможности», «Продажи»; вкладка с ошибкой отмечена, а сохранение с
   ошибками открывает первую такую вкладку. У «Нового срока» и копии по три-четыре
   поля — вкладки там только прятали бы их.
   Правила защиты действующих клиентов (spec «Правила правки публичного тарифа»):
   - срок, автопродление, аудитория, «после текущего» у тарифа с подписчиками
     заморожены (frozen_fields с сервера) — поля выключены, подсказка с выходом
     «Создать копию» стоит рядом с ними на своей вкладке;
   - смена цены и лимитов — только через диалог с числами из СВЕЖЕЙ карточки тарифа
     (getPublicPlan перед диалогом): список мог устареть, пока экран был открыт.
     Порядок шагов держит чистая saveFlow (publicPlans.js) — дровер только рисует;
   - скидка к помесячной цене — помощник ввода цены: в запрос уходит только price_rub;
   - созданный тариф всегда снят с продажи (D3) — экран говорит об этом заранее. */
import React from 'react';
import { Pill } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { Field, TextInput, Textarea, NumberInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { fmt } from '../../lib/format.js';
import * as api from '../../lib/api/salesConsole.js';
import { FEATURE_LABEL, KNOWN_FEATURES, LIMIT_FIELDS, FeaturesField } from './planForm.jsx';
import {
  PLAN_TABS, discountBaseOf, withCustomDays, withDiscount, withPeriodChoice, withPrice,
  withTermBase, withTermChoice, withTermMonths,
} from './planEditor.js';
import {
  AudienceField, DiscountField, EditorTabs, FrozenNote, PeriodField, TabPanel, TermMonthsField, Toggle, useEditorTabs,
} from './planEditorParts.jsx';
import {
  fieldLocks, frozenHint, priceHint, saleStatus, saveFlow, toPublicForm, changedKeys, treeContext,
  termSlug, termName, mskInputToIso, windowLabel, discountContext,
} from './publicPlans.js';

const { useState, useMemo } = React;

// Что на самом деле закрывает «Продаётся» (сервер: витрина, /buy, UTM, выпуск
// ссылок менеджера) и чего НЕ закрывает: уже выданная ссылка менеджера держит
// условия до истечения или отзыва — обещать «не купить ни по каким ссылкам» нельзя.
export const PUBLIC_ACTIVE_HINT = 'Снятый с продажи тариф не купить в витрине, по ссылке /buy и UTM-ссылкам, и по нему нельзя выпустить новую ссылку менеджера. Уже выданные ссылки менеджера оплачиваются до истечения или отзыва.';

/* ── Поле даты окна продаж (по Москве) ── */
function SaleDateField({ label, value, onChange, error }) {
  return (
    <Field label={label} error={error}>
      <div className="sc-pub-date">
        <TextInput type="datetime-local" value={value} invalid={!!error}
          onChange={(e) => onChange(e.target.value)} aria-label={label} />
        {value && <Button size="sm" variant="ghost" onClick={() => onChange('')}>Без даты</Button>}
      </div>
    </Field>
  );
}

/* ── Вкладка «Основное» ── */
function MainTab({ isEdit, form, errors, set, locks, frozen, onCopyInstead }) {
  return (
    <>
      <Field label="Название" hint="Видит клиент — в витрине, в счёте и на странице оплаты." error={errors.name} required>
        <TextInput value={form.name} invalid={!!errors.name} placeholder="Expert · ноябрьская акция"
          onChange={(e) => set('name')(e.target.value)} />
      </Field>
      {locks.audience && <FrozenNote hint={frozen} onCopyInstead={onCopyInstead} />}
      <AudienceField value={form.audience} onChange={set('audience')} disabled={!!locks.audience} error={errors.audience} />
      {/* Слаг — только при создании: после него он не меняется (на него ссылаются
          платежи, /buy и UTM-метки) и виден в шапке карточки. */}
      {!isEdit && (
        <Field label="Слаг" error={errors.slug} required
          hint="Латиница в нижнем регистре, цифры, «-» и «_». После создания не меняется. Срок существующего тарифа заводите кнопкой «Новый срок».">
          <TextInput value={form.slug} invalid={!!errors.slug} placeholder="expert_promo_nov"
            onChange={(e) => set('slug')(e.target.value)} autoComplete="off" spellCheck={false} />
        </Field>
      )}
      <Field label="Пометка для продаж" hint="Внутренняя заметка: зачем тариф и кому его предлагать. Клиент её не видит." error={errors.sales_note}>
        <Textarea value={form.sales_note} minRows={2} invalid={!!errors.sales_note}
          onChange={(e) => set('sales_note')(e.target.value)} />
      </Field>
    </>
  );
}

/* ── Вкладка «Цена и срок» ── */
function PriceTab({ isEdit, plan, form, errors, set, locks, frozen, onCopyInstead, base, months, showDiscount, onPeriod, onDays, onDiscount, onPrice }) {
  return (
    <>
      {(locks.period_days || locks.renewable) && <FrozenNote hint={frozen} onCopyInstead={onCopyInstead} />}
      <PeriodField form={form} onChoice={onPeriod} onDays={onDays} disabled={!!locks.period_days} error={errors.period_days} />
      {showDiscount && <DiscountField form={form} base={base} months={months} onChange={onDiscount} />}
      <Field label="Цена за срок" error={errors.price_rub} required
        hint={isEdit ? priceHint(plan) : 'Сколько клиент платит за весь срок тарифа.'}>
        <NumberInput value={form.price_rub} unit="₽" min={0} invalid={!!errors.price_rub} onChange={onPrice}
          aria-label="Цена за срок" />
      </Field>
      <div className="sc-plans-toggles">
        <Toggle title="Автопродление" checked={form.renewable} onChange={set('renewable')}
          disabled={!!locks.renewable} error={errors.renewable}
          hint="Подписка продлевается сама, пока клиент не отменит. Выключено — разовый доступ на срок." />
      </div>
    </>
  );
}

/* ── Вкладка «Лимиты и возможности» ── */
function LimitsTab({ isEdit, plan, form, errors, set, setFeatures, setFeatureError, labels, knownKeys }) {
  const ws = isEdit ? plan.workspaces_count : 0;
  return (
    <>
      {isEdit && ws > 0 && (
        <div className="sc-plans-warn sc-plans-warn--info" role="status">
          <Icon name="info" size={16} />
          <span>Лимиты и фичи действуют сразу для всех на тарифе — сейчас это {ws}{' '}
            {fmt.plural(ws, 'пространство', 'пространства', 'пространств')}. Перед сохранением покажем точное число.</span>
        </div>
      )}
      <div className="sc-plans-note">
        <Icon name="info" size={14} />
        <span>В лимитах <b className="num">-1</b> — безлимит, <b className="num">0</b> — функция на тарифе запрещена.
          У размера файла безлимита нет: только положительное число.</span>
      </div>
      <div className="sc-plans-grid">
        {LIMIT_FIELDS.map(({ key, label, unit, min, hint }) => (
          <Field key={key} label={label} hint={hint} error={errors[key]}>
            <NumberInput value={form[key]} unit={unit || undefined} min={min}
              invalid={!!errors[key]} onChange={set(key)} aria-label={label} />
          </Field>
        ))}
      </div>
      <FeaturesField rows={form.features} onChange={setFeatures} error={errors.features}
        onError={setFeatureError} labels={labels} knownKeys={knownKeys} />
    </>
  );
}

/* ── Вкладка «Продажи» ── */
function SalesTab({ isEdit, plan, form, errors, set, locks, frozen, onCopyInstead }) {
  const preview = windowLabel(mskInputToIso(form.sale_starts_at), mskInputToIso(form.sale_ends_at));
  return (
    <>
      <div className="sc-plans-toggles">
        {isEdit && (
          <Toggle title="Продаётся" checked={form.is_active} onChange={set('is_active')}
            disabled={!!plan.is_default} error={errors.is_active}
            hint={plan.is_default
              ? 'Это тариф по умолчанию — его выдают каждой новой регистрации, снять его с продажи нельзя.'
              : PUBLIC_ACTIVE_HINT} />
        )}
        <Toggle title="Показывать в витрине" checked={form.show_in_storefront} onChange={set('show_in_storefront')}
          error={errors.show_in_storefront}
          hint="Выключено — тарифа нет в «Настройки → Тариф» и в мастере заведения, но он продаётся по прямой ссылке, ссылке менеджера и UTM-ссылке. Свой текущий тариф клиент видит всегда." />
      </div>
      {!isEdit && (
        <div className="sc-plans-warn sc-plans-warn--info" role="status">
          <Icon name="info" size={16} />
          <span>Новый тариф создаётся <b>снятым с продажи</b>. Проверьте условия и включите «Продаётся» в его карточке, когда будете готовы.</span>
        </div>
      )}
      <section className="sc-pub-block">
        <h3 className="sc-pub-block__title">Окно продаж</h3>
        <div className="sc-plans-grid">
          <SaleDateField label="Начало продаж, МСК" value={form.sale_starts_at} onChange={set('sale_starts_at')}
            error={errors.sale_starts_at} />
          <SaleDateField label="Конец продаж, МСК" value={form.sale_ends_at} onChange={set('sale_ends_at')}
            error={errors.sale_ends_at} />
        </div>
        <p className="sc-pub-caption t-caption">
          Время московское. Конец не включается: «22.10 00:00» — последний день продаж 21.10.
          Без дат тариф продаётся, пока включён.
          {preview && <> Сейчас: <b>{preview}</b>.</>}
        </p>
      </section>
      <section className="sc-pub-block">
        <h3 className="sc-pub-block__title">Начало подписки</h3>
        {locks.starts_after_current && <FrozenNote hint={frozen} onCopyInstead={onCopyInstead} />}
        <div className="sc-plans-toggles">
          <Toggle title="Начинается после текущего" checked={form.starts_after_current}
            onChange={set('starts_after_current')} disabled={!!locks.starts_after_current} error={errors.starts_after_current}
            hint="Оплаченный тариф встаёт в очередь и включается, когда закончится текущий платный срок клиента — как у акций." />
        </div>
      </section>
    </>
  );
}

/* ── Новый срок существующего тарифа ── */
// Цена основы — за ЕЁ срок: основа «Нового срока» не обязана быть месячной, и «за месяц»
// у тарифа на 60 дней назвало бы помесячной цену двух месяцев.
function basePriceHint(plan) {
  const days = Number(plan.period_days);
  const per = days === 30 || !days ? 'за месяц' : `за ${days} ${fmt.plural(days, 'день', 'дня', 'дней')}`;
  return `Цена тарифа «${plan.name}» ${per} — ${fmt.money(Number(plan.price_rub))}.`;
}

function TermFields({ form, errors, ctx, base, months, showDiscount, onBase, onChoice, onMonths, onDiscount, onPrice }) {
  const plan = ctx.bySlug.get(form.base_slug) || null;
  const ready = !!plan && Number.isInteger(months) && months >= 2 && months <= 36;
  return (
    <>
      <Field label="Тариф" error={errors.base_slug} required
        hint="К какому тарифу добавляется срок: лимиты, фичи, аудитория и автопродление берутся у него.">
        <Select value={form.base_slug} onChange={onBase} invalid={!!errors.base_slug}
          options={ctx.termBases.map(({ value, label, group }) => ({ value, label, group }))}
          searchable={false} ariaLabel="Тариф, к которому добавляется срок" placeholder="Выберите тариф" />
      </Field>
      <TermMonthsField form={form} onChoice={onChoice} onMonths={onMonths} error={errors.months} />
      {showDiscount && <DiscountField form={form} base={base} months={months} onChange={onDiscount} />}
      <Field label="Цена за весь срок" error={errors.price_rub} required
        hint={plan ? basePriceHint(plan) : undefined}>
        <NumberInput value={form.price_rub} unit="₽" min={0} invalid={!!errors.price_rub} onChange={onPrice}
          aria-label="Цена за весь срок" />
      </Field>
      {ready && (
        <div className="sc-plans-note" role="status">
          <Icon name="info" size={14} />
          <span>
            Будет создан <b>{termName(plan.name, months)}</b> · слаг <span className="num">{termSlug(plan.slug, months)}</span> ·{' '}
            {months * 30} дней. Цену за месяц и скидку в списке посчитает сервер.
          </span>
        </div>
      )}
      <div className="sc-plans-warn sc-plans-warn--info" role="status">
        <Icon name="info" size={16} />
        <span>Новый срок создаётся <b>снятым с продажи</b> — включите «Продаётся» в его карточке, когда будете готовы.</span>
      </div>
    </>
  );
}

/* ── Копия тарифа ── */
function CopyFields({ form, errors, set, source }) {
  return (
    <>
      <div className="sc-plans-note">
        <Icon name="layers" size={14} />
        <span>
          Скопируются все условия «{source.name}» (<span className="num">{source.slug}</span>): цена, срок, лимиты,
          фичи, витрина, окно продаж, пометка. На копии никого нет — её условия можно менять свободно.
        </span>
      </div>
      <Field label="Слаг копии" error={errors.slug} required
        hint="Латиница в нижнем регистре, цифры, «-» и «_». После создания не меняется.">
        <TextInput value={form.slug} invalid={!!errors.slug} placeholder={`${source.slug}_b`}
          onChange={(e) => set('slug')(e.target.value)} autoComplete="off" spellCheck={false} />
      </Field>
      <Field label="Название копии" error={errors.name}
        hint="Пусто — останется название оригинала. Видит клиент — в витрине и на странице оплаты.">
        <TextInput value={form.name} invalid={!!errors.name} onChange={(e) => set('name')(e.target.value)} />
      </Field>
      <div className="sc-plans-warn sc-plans-warn--info" role="status">
        <Icon name="info" size={16} />
        <span>Копия создаётся <b>снятой с продажи</b> — включите «Продаётся» в её карточке, когда будете готовы.</span>
      </div>
    </>
  );
}

/* ── Метки в шапке карточки ──
   Статус — той же функцией, что в строке дерева (saleStatus): иначе у тарифа с
   закрытым окном продаж дерево писало бы «окно закрыто», а карточка — «Продаётся». */
export function PlanHeadTags({ plan, now }) {
  const st = saleStatus(plan, now);
  return (
    <div className="sc-pub-head-tags cluster cluster-3">
      <Pill tone={st.tone} size="sm">{st.label}</Pill>
      {st.note && <span className="sc-pub-sub">{st.note}</span>}
      {plan.is_default && <Pill tone="accent" size="sm" glyph={false}>По умолчанию</Pill>}
      {plan.is_promo && <Pill tone="accent" size="sm" glyph={false}>Акция</Pill>}
    </div>
  );
}

const TITLES = {
  term: 'Новый срок тарифа',
  new: 'Новый тариф / акция',
};

/* ── Дровер ── */
export function PublicPlanDrawer({ mode, plan, source, baseKey, data, onClose, onSaved, onCopyInstead }) {
  const toast = useToast();
  const confirm = useConfirm();
  const catalog = (data && data.features_catalog) || [];
  const ctx = useMemo(() => treeContext(data), [data]);
  const labels = useMemo(
    () => ({ ...FEATURE_LABEL, ...Object.fromEntries(catalog.map((c) => [c.key, c.label])) }), [catalog]);
  const knownKeys = catalog.length ? catalog.map((c) => c.key) : KNOWN_FEATURES;
  const isEdit = mode === 'edit';
  const tabbed = mode === 'edit' || mode === 'new';

  // Основа скидки и месяцы — одним правилом с проверкой формы (discountContext): у
  // срока семейства — его основа (expert_6m → expert), у «Нового срока» — выбранный
  // тариф. У нового тарифа своего семейства ещё нет.
  const discountOf = (f) => discountContext(mode, f, plan, ctx);

  // У «Нового срока» цены ещё нет — открывать поле скидки уже с числом нечем.
  const [initial] = useState(() => toPublicForm(mode, {
    plan, source, baseKey, catalog, base: isEdit ? discountOf(null).base : null,
  }));
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const { tab, setTab, ref, showErrors } = useEditorTabs();

  const clear = (...keys) => {
    setErrors((e) => ({ ...e, ...Object.fromEntries(keys.map((k) => [k, ''])) }));
    setFormError('');
  };
  const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); clear(k); };
  const setFeatures = (rows) => { setForm((f) => ({ ...f, features: rows })); setErrors((e) => ({ ...e, features: '' })); };
  const setFeatureError = (m) => setErrors((e) => ({ ...e, features: m }));
  // Срок, скидка и цена связаны — переход меняет форму целиком (planEditor.js). Каждый
  // из них снимает и ошибку скидки: срок мог спрятать поле, цена — вывести скидку
  // заново, а отметка на вкладке без видимой ошибки звала бы искать несуществующее.
  const move = (fn, ...keys) => { setForm((f) => fn(f)); clear(...keys, 'discount_pct'); };
  const onPeriod = (c) => move((f) => withPeriodChoice(f, c, discountOf(f).base), 'period_days', 'price_rub');
  const onDays = (v) => move((f) => withCustomDays(f, v, discountOf(f).base), 'period_days', 'price_rub');
  const onDiscount = (v) => move((f) => { const d = discountOf(f); return withDiscount(f, v, d.base, d.months); }, 'price_rub');
  const onPrice = (v) => move((f) => { const d = discountOf(f); return withPrice(f, v, d.base, d.months); }, 'price_rub');
  const onTermChoice = (c) => move((f) => withTermChoice(f, c, discountOf(f).base), 'months', 'price_rub');
  const onTermMonths = (v) => move((f) => withTermMonths(f, v, discountOf(f).base), 'months', 'price_rub');
  const onTermBase = (slug) => move((f) => withTermBase(f, slug, discountBaseOf(ctx.bySlug.get(slug))), 'base_slug', 'months', 'price_rub');

  const keys = isEdit ? changedKeys(initial, form, plan) : [];
  const { base, months, shown: showDiscount } = discountOf(form);
  const locks = fieldLocks(plan, mode);
  const frozen = isEdit && Object.keys(locks).length ? frozenHint(plan) : '';

  // Порядок «проверка → свежая карточка → диалог → запрос» — в saveFlow; здесь
  // только раскладка итога по экрану (и по вкладкам).
  const save = async () => {
    const r = await saveFlow({
      mode, form, initial, plan, ctx, api, busy: setSaving,
      confirm: (ask) => confirm({
        title: ask.title,
        body: <div className="stack stack-8">{ask.lines.map((l) => <p key={l} className="sc-pub-confirm-line">{l}</p>)}</div>,
        confirmLabel: 'Сохранить',
      }),
    });
    if (r.status === 'invalid') {
      setErrors(r.errors);
      setFormError('Проверьте отмеченные поля.');
      showErrors(r.errors);
    } else if (r.status === 'noop') {
      onClose();
    } else if (r.status === 'error') {
      setErrors((e) => ({ ...e, ...r.error.fields }));
      setFormError(r.error.detail);
      toast.show(r.error.detail, 'danger');
      showErrors(r.error.fields);
    } else if (r.status === 'saved') {
      onSaved({ kind: r.kind, slug: r.slug, message: r.message });
    }
  };

  const title = isEdit ? (form.name || plan.slug)
    : mode === 'copy' ? `Копия тарифа «${source.name}»` : TITLES[mode];
  const crumbs = isEdit ? `Публичный тариф · ${plan.slug}` : 'Публичные тарифы';

  const footer = (
    <>
      <Button variant="ghost" onClick={onClose} disabled={saving}>Отмена</Button>
      <div className="grow" />
      <Button variant="primary" loading={saving} onClick={save} disabled={isEdit && keys.length === 0}>
        {isEdit ? 'Сохранить' : 'Создать'}
      </Button>
    </>
  );

  const shared = { isEdit, plan, form, errors, set, locks, frozen, onCopyInstead };
  const label = (v) => PLAN_TABS.find((t) => t.value === v).label;

  return (
    <Drawer open onClose={onClose} size="lg" footer={footer} title={title} breadcrumbs={crumbs}>
      <div className="sc-plans-form stack stack-12" ref={ref}>
        {isEdit && <PlanHeadTags plan={plan} />}
        {formError && (
          <div className="sc-plans-error" role="alert"><Icon name="alert-circle" size={14} /><span>{formError}</span></div>
        )}
        {mode === 'term' && (
          <TermFields form={form} errors={errors} ctx={ctx} base={base} months={months} showDiscount={showDiscount}
            onBase={onTermBase} onChoice={onTermChoice} onMonths={onTermMonths} onDiscount={onDiscount} onPrice={onPrice} />
        )}
        {mode === 'copy' && <CopyFields form={form} errors={errors} set={set} source={source} />}
        {tabbed && (
          <>
            <EditorTabs value={tab} onChange={setTab} errors={errors} />
            <TabPanel value="main" label={label('main')} active={tab === 'main'}>
              <MainTab {...shared} />
            </TabPanel>
            <TabPanel value="price" label={label('price')} active={tab === 'price'}>
              <PriceTab {...shared} base={base} months={months} showDiscount={showDiscount}
                onPeriod={onPeriod} onDays={onDays} onDiscount={onDiscount} onPrice={onPrice} />
            </TabPanel>
            <TabPanel value="limits" label={label('limits')} active={tab === 'limits'}>
              <LimitsTab {...shared} setFeatures={setFeatures} setFeatureError={setFeatureError}
                labels={labels} knownKeys={knownKeys} />
            </TabPanel>
            <TabPanel value="sales" label={label('sales')} active={tab === 'sales'}>
              <SalesTab {...shared} />
            </TabPanel>
          </>
        )}
      </div>
    </Drawer>
  );
}
